import { useState, useEffect, useCallback } from 'react';
import {
    CONFIG_SECTIONS,
    getSectionCounts,
    exportConfigSections,
    downloadBundle,
} from '../../api/configTransferApi';

interface UseConfigExportReturn {
    counts: Record<string, number | null>;
    loadingCounts: boolean;
    selected: Set<string>;
    toggleSection: (key: string) => void;
    exporting: boolean;
    exportSelected: () => Promise<void>;
    warnings: string[];
    error: string | null;
    success: boolean;
}

export const useConfigExport = (): UseConfigExportReturn => {
    const [counts, setCounts] = useState<Record<string, number | null>>({});
    const [loadingCounts, setLoadingCounts] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [exporting, setExporting] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getSectionCounts()
            .then(result => {
                if (cancelled) return;
                setCounts(result);
                // Preselect every section that has data
                setSelected(new Set(
                    CONFIG_SECTIONS.filter(s => (result[s.key] ?? 0) > 0).map(s => s.key)
                ));
            })
            .catch(err => {
                console.error('Error loading section counts:', err);
                if (!cancelled) setError('Kunde inte läsa konfigurationsdata');
            })
            .finally(() => {
                if (!cancelled) setLoadingCounts(false);
            });
        return () => { cancelled = true; };
    }, []);

    const toggleSection = useCallback((key: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const exportSelected = useCallback(async () => {
        setError(null);
        setWarnings([]);
        setSuccess(false);
        setExporting(true);
        try {
            const { bundle, warnings: exportWarnings } = await exportConfigSections(Array.from(selected));
            if (Object.keys(bundle.sections).length === 0) {
                setError('Ingen data kunde exporteras för valda sektioner');
                setWarnings(exportWarnings);
                return;
            }
            downloadBundle(bundle);
            setWarnings(exportWarnings);
            setSuccess(true);
        } catch (err) {
            console.error('Error exporting configuration:', err);
            setError('Exporten misslyckades. Försök igen.');
        } finally {
            setExporting(false);
        }
    }, [selected]);

    return {
        counts,
        loadingCounts,
        selected,
        toggleSection,
        exporting,
        exportSelected,
        warnings,
        error,
        success,
    };
};
